import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, LineChart, Line, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { insuranceApiMethods, titleApiMethods } from '../utils/api';
import './Dashboard.css';

function Dashboard() {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('all'); // 'all', 'insurance', 'title'
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    extracting: 0,
    autoApproving: 0,
    complete: 0,
    approved: 0,
    rejected: 0
  });
  const [recentDocuments, setRecentDocuments] = useState([]);
  const [allDocuments, setAllDocuments] = useState([]);
  const [companyDistribution, setCompanyDistribution] = useState([]);
  const [documentTypeDistribution, setDocumentTypeDistribution] = useState([]);
  const [approvalTrend, setApprovalTrend] = useState([]);
  const [trendPeriod, setTrendPeriod] = useState(7); // 1, 7, or 30 days
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchDashboardData = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Fetch from both APIs in parallel using authenticated clients
      const [insuranceResponse, titleResponse] = await Promise.all([
        insuranceApiMethods.getDocuments().catch(err => {
          console.error('Error fetching insurance data:', err);
          return { data: { items: [] } };
        }),
        titleApiMethods.getDocuments().catch(err => {
          console.error('Error fetching title data:', err);
          return { data: { items: [] } };
        })
      ]);

      const insuranceData = insuranceResponse.data || { items: [] };
      const titleData = titleResponse.data || { items: [] };
      
      const insuranceDocs = (insuranceData.items || []).map(doc => ({ ...doc, documentType: 'Insurance' }));
      const titleDocs = (titleData.items || []).map(doc => ({ ...doc, documentType: 'Title' }));
      
      // Combine all documents
      const allDocs = [...insuranceDocs, ...titleDocs];
      
      setAllDocuments(allDocs);
      calculateDocumentTypeDistribution(insuranceDocs.length, titleDocs.length);
      
      setLastUpdate(new Date());
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setIsLoading(false);
    }
  }, []);

  // Filter documents based on active view
  const getFilteredDocuments = useCallback(() => {
    if (activeView === 'insurance') {
      return allDocuments.filter(doc => doc.documentType === 'Insurance');
    } else if (activeView === 'title') {
      return allDocuments.filter(doc => doc.documentType === 'Title');
    }
    return allDocuments;
  }, [allDocuments, activeView]);

  // Update stats and charts when view changes
  useEffect(() => {
    const filtered = getFilteredDocuments();
    calculateStats(filtered);
    calculateCompanyDistribution(filtered);
    
    // Get recent documents
    const sorted = [...filtered].sort((a, b) => 
      (b.initial_timestamp || 0) - (a.initial_timestamp || 0)
    );
    setRecentDocuments(sorted.slice(0, 6));
  }, [allDocuments, activeView, getFilteredDocuments]);

  useEffect(() => {
    if (allDocuments.length > 0) {
      const filtered = getFilteredDocuments();
      calculateApprovalTrend(filtered, trendPeriod);
    }
  }, [allDocuments, trendPeriod, activeView, getFilteredDocuments]);

  const calculateStats = (docs) => {
    const newStats = {
      total: docs.length,
      pending: 0,
      extracting: 0,
      autoApproving: 0,
      complete: 0,
      approved: 0,
      rejected: 0,
      inProgress: 0
    };

    docs.forEach(doc => {
      const extractionStatus = doc.extraction_status || '';
      const autoApprovalStatus = doc.auto_approval_status || '';

      // Status counts
      if (extractionStatus.includes('pending')) {
        newStats.pending++;
        newStats.inProgress++;
      } else if (extractionStatus.includes('complete') && autoApprovalStatus.includes('pending')) {
        newStats.extracting++;
        newStats.inProgress++;
      } else if (autoApprovalStatus.includes('in progress')) {
        newStats.autoApproving++;
        newStats.inProgress++;
      } else if (autoApprovalStatus.includes('complete')) {
        newStats.complete++;
      }

      // Approval decision counts
      const decision = getApprovalDecision(doc);
      if (decision) {
        const lowerDecision = decision.toLowerCase();
        if (lowerDecision.includes('not approved') || lowerDecision.includes('reject')) {
          newStats.rejected++;
        } else if (lowerDecision.includes('approved')) {
          newStats.approved++;
        }
      }
    });

    setStats(newStats);
  };

  const getApprovalDecision = (doc) => {
    try {
      let results = doc.auto_approval_results;
      if (typeof results === 'string') {
        results = JSON.parse(results);
      }
      return results?.final_decision || null;
    } catch (error) {
      return null;
    }
  };

  const calculateCompanyDistribution = (docs) => {
    const companyCount = {};
    docs.forEach(doc => {
      const company = doc.company || 'Unknown';
      companyCount[company] = (companyCount[company] || 0) + 1;
    });

    const distribution = Object.entries(companyCount)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    setCompanyDistribution(distribution);
  };

  const calculateDocumentTypeDistribution = (insuranceCount, titleCount) => {
    setDocumentTypeDistribution([
      { name: 'Insurance', value: insuranceCount, color: '#3b82f6' },
      { name: 'Title', value: titleCount, color: '#8b5cf6' }
    ]);
  };

  const calculateApprovalTrend = useCallback((docs, days) => {
    const now = Date.now() / 1000;
    const startTime = now - (days * 24 * 60 * 60);
    
    const filteredDocs = docs.filter(doc => 
      doc.initial_timestamp && 
      doc.initial_timestamp >= startTime &&
      doc.auto_approval_status && 
      doc.auto_approval_status.includes('complete')
    );

    const dayGroups = {};
    
    for (let i = 0; i < days; i++) {
      const date = new Date((now - (i * 24 * 60 * 60)) * 1000);
      const dayKey = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      if (!dayGroups[dayKey]) {
        dayGroups[dayKey] = { date: dayKey, approved: 0, notApproved: 0, timestamp: date.getTime() };
      }
    }
    
    filteredDocs.forEach(doc => {
      const date = new Date(doc.initial_timestamp * 1000);
      const dayKey = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      if (!dayGroups[dayKey]) {
        dayGroups[dayKey] = { date: dayKey, approved: 0, notApproved: 0, timestamp: date.getTime() };
      }
      
      const decision = getApprovalDecision(doc);
      if (decision) {
        const lowerDecision = decision.toLowerCase();
        if (lowerDecision.includes('not approved') || lowerDecision.includes('reject')) {
          dayGroups[dayKey].notApproved++;
        } else if (lowerDecision.includes('approved')) {
          dayGroups[dayKey].approved++;
        }
      }
    });

    const trend = Object.values(dayGroups)
      .sort((a, b) => a.timestamp - b.timestamp)
      .slice(-days);
    
    setApprovalTrend(trend);
  }, []);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '--';
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff / (1000 * 60));
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString();
  };

  const getStatusBadgeClass = (extractionStatus, autoApprovalStatus) => {
    if (autoApprovalStatus && autoApprovalStatus.includes('complete')) {
      return 'status-complete';
    } else if (autoApprovalStatus && autoApprovalStatus.includes('in progress')) {
      return 'status-in-progress';
    } else if (extractionStatus && extractionStatus.includes('complete')) {
      return 'status-in-progress';
    } else if (extractionStatus && extractionStatus.includes('pending')) {
      return 'status-pending';
    }
    return 'status-pending';
  };

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  const getViewTitle = () => {
    switch (activeView) {
      case 'insurance':
        return 'Insurance Documents';
      case 'title':
        return 'Title Documents';
      default:
        return 'All Documents';
    }
  };

  const navigateToMonitor = (doc) => {
    if (doc.documentType === 'Title') {
      navigate('/title/monitor', { state: { openDocId: doc.doc_id } });
    } else {
      navigate('/insurance/monitor', { state: { openDocId: doc.doc_id } });
    }
  };

  return (
    <div className="dashboard-container">
      {/* Hero Section */}
      <div className="dashboard-hero">
        <div className="hero-content">
          <h1>Document Auto-Approval Dashboard</h1>
          <p>Real-time Insurance & Title auto-approval workflow powered by Prajna AI</p>
          {lastUpdate && (
            <div className="hero-update">
              <i className="fas fa-sync-alt"></i>
              Last updated {lastUpdate.toLocaleTimeString()}
            </div>
          )}
        </div>
      </div>

      {/* View Filter Tabs */}
      <div className="view-filter-tabs">
        <button 
          className={`view-tab ${activeView === 'all' ? 'active' : ''}`}
          onClick={() => setActiveView('all')}
        >
          <i className="fas fa-th-large"></i>
          All Documents
          <span className="tab-badge">{allDocuments.length}</span>
        </button>
        <button 
          className={`view-tab ${activeView === 'insurance' ? 'active' : ''}`}
          onClick={() => setActiveView('insurance')}
        >
          <i className="fas fa-shield-alt"></i>
          Insurance
          <span className="tab-badge">{allDocuments.filter(d => d.documentType === 'Insurance').length}</span>
        </button>
        <button 
          className={`view-tab ${activeView === 'title' ? 'active' : ''}`}
          onClick={() => setActiveView('title')}
        >
          <i className="fas fa-file-contract"></i>
          Title
          <span className="tab-badge">{allDocuments.filter(d => d.documentType === 'Title').length}</span>
        </button>
      </div>

      {/* Overview Stats - Horizontal Row */}
      <div className="overview-stats-horizontal">
        <div className="stat-card-horizontal primary">
          <div className="stat-icon-circle">
            <i className="fas fa-file-alt"></i>
          </div>
          <div className="stat-info">
            <div className="stat-label">Total</div>
            <div className="stat-value-lg">{stats.total}</div>
          </div>
        </div>

        <div className="stat-card-horizontal info">
          <div className="stat-icon-circle">
            <i className="fas fa-cogs"></i>
          </div>
          <div className="stat-info">
            <div className="stat-label">In Progress</div>
            <div className="stat-value-lg">{stats.inProgress}</div>
          </div>
        </div>

        <div className="stat-card-horizontal secondary">
          <div className="stat-icon-circle">
            <i className="fas fa-check-double"></i>
          </div>
          <div className="stat-info">
            <div className="stat-label">Complete</div>
            <div className="stat-value-lg">{stats.complete}</div>
          </div>
        </div>

        <div className="stat-card-horizontal success">
          <div className="stat-icon-circle">
            <i className="fas fa-check-circle"></i>
          </div>
          <div className="stat-info">
            <div className="stat-label">Approved</div>
            <div className="stat-value-lg">{stats.approved}</div>
          </div>
        </div>

        <div className="stat-card-horizontal danger">
          <div className="stat-icon-circle">
            <i className="fas fa-times-circle"></i>
          </div>
          <div className="stat-info">
            <div className="stat-label">Rejected</div>
            <div className="stat-value-lg">{stats.rejected}</div>
          </div>
        </div>
      </div>

      {/* Main Content - 2 Column Layout */}
      <div className="dashboard-main-content">
        {/* Left Column: Distribution Box with All Visualizations */}
        <div className="dashboard-left-column">
          <div className="distribution-box">
            <div className="distribution-header">
              <h3>{getViewTitle()} - Distribution</h3>
              <button className="refresh-icon" onClick={fetchDashboardData} title="Refresh">
                <i className="fas fa-sync-alt"></i>
              </button>
            </div>

            {/* Pie Charts */}
            <div className={`pie-charts-row ${activeView === 'all' ? 'three-charts' : 'two-charts'}`}>
              {/* Show Document Type chart only in "All" view */}
              {activeView === 'all' && (
                <div className="pie-chart-container">
                  <h4 className="chart-title">Document Type</h4>
                  <div className="pie-chart-wrapper">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={documentTypeDistribution}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ percent }) => percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ''}
                          outerRadius={38}
                          innerRadius={24}
                          fill="#8884d8"
                          dataKey="value"
                          paddingAngle={2}
                        >
                          {documentTypeDistribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ 
                            background: 'white', 
                            border: 'none',
                            borderRadius: '8px',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                            padding: '12px'
                          }}
                        />
                        <Legend 
                          iconType="circle"
                          iconSize={8}
                          wrapperStyle={{ 
                            fontSize: '0.75rem',
                            paddingTop: '0.5rem'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Status Distribution Chart */}
              <div className="pie-chart-container">
                <h4 className="chart-title">Status</h4>
                <div className="pie-chart-wrapper">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'In Progress', value: stats.inProgress, color: '#2563eb' },
                          { name: 'Approved', value: stats.approved, color: '#16a34a' },
                          { name: 'Rejected', value: stats.rejected, color: '#dc2626' },
                        ]}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ percent }) => percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ''}
                        outerRadius={38}
                        innerRadius={24}
                        fill="#8884d8"
                        dataKey="value"
                        paddingAngle={2}
                      >
                        {[
                          { name: 'In Progress', value: stats.inProgress, color: '#2563eb' },
                          { name: 'Approved', value: stats.approved, color: '#16a34a' },
                          { name: 'Rejected', value: stats.rejected, color: '#dc2626' },
                        ].map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          background: 'white', 
                          border: 'none',
                          borderRadius: '8px',
                          boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                          padding: '12px'
                        }}
                      />
                      <Legend 
                        iconType="circle"
                        iconSize={8}
                        wrapperStyle={{ 
                          fontSize: '0.75rem',
                          paddingTop: '0.5rem'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Company Distribution Chart */}
              <div className="pie-chart-container">
                <h4 className="chart-title">Company</h4>
                <div className="pie-chart-wrapper">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={companyDistribution}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ percent }) => percent > 0.08 ? `${(percent * 100).toFixed(0)}%` : ''}
                        outerRadius={38}
                        innerRadius={24}
                        fill="#8884d8"
                        dataKey="value"
                        paddingAngle={2}
                      >
                        {companyDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981'][index % 5]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          background: 'white', 
                          border: 'none',
                          borderRadius: '8px',
                          boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                          padding: '12px'
                        }}
                      />
                      <Legend 
                        iconType="circle"
                        iconSize={8}
                        wrapperStyle={{ 
                          fontSize: '0.75rem',
                          paddingTop: '0.5rem'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Line Chart */}
            <div className="line-chart-container">
              <div className="chart-header-row">
                <h4 className="chart-title">Approval Results</h4>
                <div className="period-selector">
                  <button 
                    className={`period-btn ${trendPeriod === 1 ? 'active' : ''}`}
                    onClick={() => setTrendPeriod(1)}
                  >
                    1D
                  </button>
                  <button 
                    className={`period-btn ${trendPeriod === 7 ? 'active' : ''}`}
                    onClick={() => setTrendPeriod(7)}
                  >
                    7D
                  </button>
                  <button 
                    className={`period-btn ${trendPeriod === 30 ? 'active' : ''}`}
                    onClick={() => setTrendPeriod(30)}
                  >
                    30D
                  </button>
                </div>
              </div>
              <div className="line-chart-wrapper">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={approvalTrend} margin={{ top: 3, right: 15, left: -10, bottom: 3 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 10 }}
                      stroke="#86868b"
                    />
                    <YAxis 
                      tick={{ fontSize: 10 }}
                      stroke="#86868b"
                      allowDecimals={false}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        background: 'white', 
                        border: 'none',
                        borderRadius: '8px',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                        padding: '12px'
                      }}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="approved" 
                      stroke="#16a34a" 
                      strokeWidth={2}
                      dot={{ fill: '#16a34a', r: 3 }}
                      activeDot={{ r: 5 }}
                      name="Approved"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="notApproved" 
                      stroke="#dc2626" 
                      strokeWidth={2}
                      dot={{ fill: '#dc2626', r: 3 }}
                      activeDot={{ r: 5 }}
                      name="Not Approved"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Recent Activity */}
        <div className="dashboard-right-column">
          <div className="column-header">
            <h3>Recent Activity</h3>
            <button 
              className="view-all-btn"
              onClick={() => {
                if (activeView === 'title') {
                  navigate('/title/monitor');
                } else if (activeView === 'insurance') {
                  navigate('/insurance/monitor');
                } else {
                  navigate('/insurance/monitor');
                }
              }}
            >
              View All
              <i className="fas fa-arrow-right"></i>
            </button>
          </div>

        <div className="activity-content">
          {isLoading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading recent documents...</p>
            </div>
          ) : recentDocuments.length === 0 ? (
            <div className="empty-state-compact">
              <i className="fas fa-inbox"></i>
              <p>No documents yet</p>
            </div>
          ) : (
            <div className="activity-list-compact">
              {recentDocuments.slice(0, 5).map((doc, index) => (
                <div 
                  key={index} 
                  className="activity-item-compact"
                  onClick={() => navigateToMonitor(doc)}
                >
                  <div className="activity-item-icon">
                    <i className={`fas ${doc.documentType === 'Title' ? 'fa-file-contract' : 'fa-shield-alt'}`}></i>
                  </div>
                  <div className="activity-item-content">
                    <div className="activity-item-name">{doc.file_name || doc.doc_id}</div>
                    <div className="activity-item-meta">
                      {activeView === 'all' && (
                        <span className={`doc-type-badge ${doc.documentType === 'Title' ? 'badge-title' : 'badge-insurance'}`}>
                          {doc.documentType}
                        </span>
                      )}
                      <span className="activity-item-company">{doc.company || '--'}</span>
                      <span className="activity-item-time">{formatTimestamp(doc.initial_timestamp)}</span>
                    </div>
                  </div>
                  <span className={`status-dot ${getStatusBadgeClass(doc.extraction_status, doc.auto_approval_status)}`}></span>
                </div>
              ))}
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
